const STORAGE_KEY = "booking-search-restorations"
const VERSION = 2
const MAX_STATES = 10
const MAX_BATCHES = 5
const MAX_STORAGE_LENGTH = 512_000
const MAX_STRING_LENGTH = 8_192
const TTL = 30 * 60 * 1000
const CLOCK_SKEW = 5 * 60 * 1000

export default class SearchRestorationStore {
  static storageKey = STORAGE_KEY

  constructor(storage = undefined, now = () => Date.now()) {
    this.storage = storage === undefined ? this.defaultStorage() : storage
    this.now = now
  }

  find(identifier, identity) {
    const states = this.readStates()
    const state = states.find((candidate) => candidate.identifier === identifier)

    if (!state) return { status: "missing" }

    if (this.expired(state)) {
      this.writeStates(states.filter((candidate) => candidate !== state))
      return { status: "expired", state }
    }

    if (state.identity !== identity) return { status: "mismatch" }

    return { status: "found", state }
  }

  save(state) {
    const states = this.readStates().filter((candidate) => {
      return candidate.identifier !== state.identifier && !this.expired(candidate)
    })
    const savedState = { ...state, updatedAt: this.now() }

    if (this.valid(savedState)) {
      this.writeStates([savedState, ...states])
      return savedState
    }

    this.writeStates(states)
  }

  readStates() {
    try {
      if (!this.storage) return []

      const value = this.storage.getItem(STORAGE_KEY)
      if (typeof value !== "string" || value.length > MAX_STORAGE_LENGTH) return []

      const stored = JSON.parse(value)
      if (stored?.version !== VERSION || !Array.isArray(stored.states)) return []

      return stored.states.slice(0, MAX_STATES).filter((state) => this.valid(state))
    } catch {
      return []
    }
  }

  writeStates(states) {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify({
        version: VERSION,
        states: states.slice(0, MAX_STATES)
      }))
    } catch {
      // Browsing still works when tab storage is unavailable or full.
    }
  }

  defaultStorage() {
    try {
      return window.sessionStorage
    } catch {
      return
    }
  }

  valid(state) {
    return state &&
      this.validString(state.identifier) &&
      this.validString(state.identity) &&
      this.validTimestamp(state.updatedAt) &&
      this.validNumber(state.scrollY, { minimum: 0 }) &&
      this.validInteger(state.rowCount) &&
      this.validAnchor(state.anchor) &&
      typeof state.replayLimited === "boolean" &&
      Array.isArray(state.batches) &&
      state.batches.length <= MAX_BATCHES &&
      state.batches.every((batch) => this.validBatch(batch))
  }

  validBatch(batch) {
    return batch &&
      this.validString(batch.url) &&
      this.validInteger(batch.rowCountBefore) &&
      this.validInteger(batch.rowCountAfter) &&
      batch.rowCountAfter > batch.rowCountBefore
  }

  validAnchor(anchor) {
    return anchor === undefined || anchor === null ||
      (this.validString(anchor.bookingId) && this.validNumber(anchor.top))
  }

  validString(value) {
    return typeof value === "string" && value.length > 0 && value.length <= MAX_STRING_LENGTH
  }

  validInteger(value) {
    return Number.isSafeInteger(value) && value >= 0
  }

  validNumber(value, { minimum = -Number.MAX_SAFE_INTEGER } = {}) {
    return Number.isFinite(value) && value >= minimum && Math.abs(value) <= Number.MAX_SAFE_INTEGER
  }

  validTimestamp(value) {
    return this.validInteger(value) && value <= this.now() + CLOCK_SKEW
  }

  expired(state) {
    return this.now() - state.updatedAt >= TTL
  }
}

export { MAX_BATCHES, MAX_STATES, STORAGE_KEY, TTL, VERSION }
